/**
 * CardRenderer — one switch that dispatches to card components by type.
 * Adding a new card = 1 new case + 1 new component.
 *
 * Phase 1 cards implemented:
 *   welcome_hero, choice_chips, login_form, signup_step, password_reset,
 *   dashboard_summary, body_map, sparkline, session_flash,
 *   workout_preview, link.
 * Deferred: macro_ring, food_log, exercise_list, workout_live_link,
 * plain_image (rendered as graceful "coming soon" placeholder).
 */
import React from 'react';
import WelcomeHeroCard from './cards/WelcomeHeroCard';
import ChoiceChipsCard from './cards/ChoiceChipsCard';
import LoginFormCard from './cards/LoginFormCard';
import SignupStepCard from './cards/SignupStepCard';
import PasswordResetCard from './cards/PasswordResetCard';
import DashboardSummaryCard from './cards/DashboardSummaryCard';
import BodyMapCard from './cards/BodyMapCard';
import SparklineCard from './cards/SparklineCard';
import SessionFlashCard from './cards/SessionFlashCard';
import LinkCard from './cards/LinkCard';
import MacroRingCard from './cards/MacroRingCard';
import FoodLogCard from './cards/FoodLogCard';
// iter69: TrainingDaysPromptCard, WeekOverviewCard, SwapOptionsCard,
// TodayWorkoutCard removed. iter71: WorkoutPreviewCard removed too —
// the daily adaptive flow now renders via TodaySessionChatCard.
import TodaySessionChatCard from './cards/TodaySessionChatCard';
import DailyReadChatCard from './cards/DailyReadChatCard';
import BehaviorProbeCard from './cards/BehaviorProbeCard';
import AssessmentPromptCard from './cards/AssessmentPromptCard';
import MobilityMapCard from './cards/MobilityMapCard';
import DailyCheckinFormCard from './cards/DailyCheckinFormCard';
import MentalSnapshotCard from './cards/MentalSnapshotCard';
import NutritionBaselineFormCard from './cards/NutritionBaselineFormCard';
import MeditationCard from './cards/MeditationCard';

const CardRenderer = ({ card, onAction }) => {
  const { type, data } = card;
  const props = { data, onAction };
  switch (type) {
    case 'welcome_hero':      return <WelcomeHeroCard {...props} />;
    case 'choice_chips':      return <ChoiceChipsCard {...props} />;
    case 'login_form':        return <LoginFormCard {...props} />;
    case 'signup_step':       return <SignupStepCard {...props} />;
    case 'password_reset':    return <PasswordResetCard {...props} />;
    case 'dashboard_summary': return <DashboardSummaryCard {...props} />;
    case 'body_map':          return <BodyMapCard {...props} />;
    case 'sparkline':         return <SparklineCard {...props} />;
    case 'session_flash':     return <SessionFlashCard {...props} />;
    case 'today_session':     return <TodaySessionChatCard {...props} />;
    case 'daily_read':        return <DailyReadChatCard {...props} />;
    case 'barrier_probe':     return <BehaviorProbeCard {...props} />;
    case 'link':              return <LinkCard {...props} />;
    case 'macro_ring':        return <MacroRingCard {...props} />;
    case 'food_log':          return <FoodLogCard {...props} />;
    // iter71: workout_preview, training_days_prompt, week_overview, swap_options,
    // today_workout card types removed — replaced by the daily today_session card.
    // Any legacy type still emitted falls through to the harmless default stub.
    case 'assessment_prompt': return <AssessmentPromptCard {...props} />;
    case 'mobility_map':      return <MobilityMapCard {...props} />;
    case 'daily_checkin_form': return <DailyCheckinFormCard {...props} />;
    case 'mental_snapshot':   return <MentalSnapshotCard {...props} />;
    case 'nutrition_baseline_form': return <NutritionBaselineFormCard {...props} />;
    case 'meditation':        return <MeditationCard {...props} />;
    default:
      return (
        <div className="text-xs text-zinc-500 italic" data-testid={`card-unknown-${type}`}>
          (Card <code>{type}</code> coming soon)
        </div>
      );
  }
};

export default CardRenderer;
